import { useState } from 'react';
import { t } from 'i18next';
import { Button } from '../../components/Button/index';
import { Input } from '../../components/Input';
import { useXimiSSOAuth } from '../../utilities/ximi/useXimiSSOAuth';
import {
    parseXimiManualEntry,
    XimiManualEntryError
} from '../../utilities/ximi/parseXimiProvisioning';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { Breakpoint } from '../../theme/breakpoints';
import { XIMI_LOGIN_SELECT_ACCOUNT_PATH } from '../../routePaths';
import styles from './XimiSelectAccount.module.css';

// Code d'erreur (renvoyé par le service) → clé i18n. Présentation pure : la
// validation vit dans parseXimiManualEntry, la page ne fait qu'afficher.
const MANUAL_ERROR_MESSAGES: Record<XimiManualEntryError, string> = {
    [XimiManualEntryError.TenantCodeRequired]: 'Veuillez saisir le code organisme',
    [XimiManualEntryError.LoginRequired]: 'Veuillez saisir votre identifiant',
    [XimiManualEntryError.TenantCodeFormat]: 'Code organisme incorrect',
    [XimiManualEntryError.UnknownEnv]: "L'environnement n'est pas reconnu"
};

const translateError = (code?: XimiManualEntryError): string | undefined =>
    code ? t(MANUAL_ERROR_MESSAGES[code]) ?? '' : undefined;

/**
 * Page de saisie manuelle (code organisme + identifiant), alternative au scan QR.
 * Web + natif. Toute la logique de contrôle est dans parseXimiManualEntry.
 */
export function XimiSsoLoginManualEntry(): JSX.Element {
    const [tenantCode, setTenantCode] = useState('');
    const [login, setLogin] = useState('');
    const [errors, setErrors] = useState<{ tenantCode?: string; login?: string }>({});
    const isMobileLandscape = useBreakpoint() === Breakpoint.MOBILE_LANDSCAPE;
    const ximiSSO = useXimiSSOAuth();

    const handleSubmit = async () => {
        const result = parseXimiManualEntry(tenantCode, login);
        if (!result.ok) {
            setErrors({
                tenantCode: translateError(result.errors.tenantCode),
                login: translateError(result.errors.login)
            });
            return;
        }
        setErrors({});
        // Pas de stockage ici : le compte est persisté après auth SSO réussie
        // (usePersistXimiAccount, depuis les claims) → la liste ne garde que les comptes valides.
        await ximiSSO.login(result.entry);
    };

    return (
        <div className={styles.container}>
            <div className={styles.panel}>
                <h1 className={styles.title}>{t('Connexion Ximi')}</h1>
                <p className={styles.subtitle}>{t('Saisie manuelle')}</p>
                <div className={styles.manualForm}>
                    <div className={styles.field}>
                        <Input
                            label={t('Code organisme') ?? ''}
                            value={tenantCode}
                            onChange={setTenantCode}
                            size="SMALL"
                            error={!!errors.tenantCode}
                        />
                        <p className={styles.hint}>{t('Exemple : Organisme/Établissement')}</p>
                        {errors.tenantCode && <p className={styles.error}>{errors.tenantCode}</p>}
                    </div>
                    <div className={styles.field}>
                        <Input
                            label={t('Identifiant') ?? ''}
                            value={login}
                            onChange={setLogin}
                            size="SMALL"
                            error={!!errors.login}
                        />
                        {errors.login && <p className={styles.error}>{errors.login}</p>}
                    </div>
                    <Button
                        label={t('Valider') ?? ''}
                        size={isMobileLandscape ? 'SMALL' : 'MEDIUM'}
                        fullWidth
                        action={handleSubmit}
                    />
                </div>

                <div className={styles.backButtonWrap}>
                    <Button
                        label={t('Retour') ?? ''}
                        theme="GREY"
                        size={isMobileLandscape ? 'SMALL' : 'LEGACY'}
                        fullWidth
                        action={XIMI_LOGIN_SELECT_ACCOUNT_PATH}
                    />
                </div>
            </div>
        </div>
    );
}
